import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, X, FilePlus, Loader2, CheckCircle2, XCircle } from 'lucide-react';

const emptyForm = {
  project_id: '',
  description: '',
  amount: '',
  vo_date: new Date().toISOString().slice(0, 10),
  reason: '',
  cost_center_id: '',
};

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const VariationOrders = ({ user }) => {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [filterProject, setFilterProject] = useState('');

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [voRes, prRes, ccRes] = await Promise.all([
        axiosInstance.get(`/variation-orders${filterProject ? `?project_id=${filterProject}` : ''}`),
        axiosInstance.get('/projects'),
        axiosInstance.get('/cost-centers').catch(() => ({ data: [] })),
      ]);
      setRows(voRes.data);
      setProjects(prRes.data);
      setCostCenters(ccRes.data);
    } catch (e) {
      toast.error(`فشل التحميل: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [filterProject]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id || !form.description.trim() || !form.amount) {
      toast.error('المشروع والوصف والمبلغ مطلوبة');
      return;
    }
    setSubmitting(true);
    try {
      await axiosInstance.post('/variation-orders', {
        ...form,
        amount: parseFloat(form.amount),
        cost_center_id: form.cost_center_id || null,
      });
      toast.success('تم إنشاء أمر التغيير. ينتظر اعتماد الإدارة.');
      setShowModal(false);
      setForm(emptyForm);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('اعتماد أمر التغيير وتعديل قيمة العقد والقيود المحاسبية؟')) return;
    try {
      const res = await axiosInstance.post(`/variation-orders/${id}/approve`);
      toast.success(`اعتُمد. القيمة الجديدة للعقد: ${fmt(res.data.new_contract_value)}`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الاعتماد');
    }
  };

  const handleReject = async (id) => {
    try {
      await axiosInstance.post(`/variation-orders/${id}/reject`);
      toast.success('تم الرفض');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('حذف أمر التغيير؟ سيُعكَس القيد المحاسبي تلقائياً.')) return;
    try {
      await axiosInstance.delete(`/variation-orders/${id}`);
      toast.success('تم الحذف');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحذف');
    }
  };

  return (
    <div className="space-y-6" data-testid="variation-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FilePlus className="w-7 h-7 text-amber-600" /> Variation Orders
          </h1>
          <p className="text-sm text-gray-500 mt-1">أوامر التغيير — تعديل قيمة العقد + قيد محاسبي تلقائي</p>
        </div>
        <div className="flex gap-2">
          <select data-testid="filter-project" value={filterProject} onChange={(e) => setFilterProject(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm">
            <option value="">All Projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button data-testid="new-vo-btn" onClick={() => setShowModal(true)}
            className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm hover:bg-amber-700 flex items-center gap-2">
            <Plus className="w-4 h-4" /> New VO
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-400">لا توجد أوامر تغيير بعد.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">VO #</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Date</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Project</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Description</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Amount</th>
                <th className="px-3 py-2 text-center text-[11px] uppercase text-gray-500 font-semibold">Status</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-sm">{r.vo_number}</td>
                  <td className="px-3 py-2 text-sm">{r.vo_date}</td>
                  <td className="px-3 py-2 text-sm">{r.project_name}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{r.description}</td>
                  <td className={`px-3 py-2 text-sm text-right font-mono font-semibold ${r.amount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {r.amount >= 0 ? '+' : ''}{fmt(r.amount)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      r.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      {r.status === 'pending' && isAdmin && (
                        <>
                          <button data-testid={`approve-vo-${r.vo_number}`} onClick={() => handleApprove(r.id)}
                            className="p-1.5 hover:bg-emerald-50 rounded text-emerald-600" title="Approve">
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button data-testid={`reject-vo-${r.vo_number}`} onClick={() => handleReject(r.id)}
                            className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Reject">
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {isAdmin && (
                        <button data-testid={`delete-vo-${r.vo_number}`} onClick={() => handleDelete(r.id)}
                          className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full" data-testid="vo-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-semibold">New Variation Order</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Project *</label>
                <select data-testid="vo-project" required value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                  <option value="">— Select —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">VO Date *</label>
                  <input type="date" data-testid="vo-date" value={form.vo_date} onChange={(e) => setForm({ ...form, vo_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" required />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Amount * (+ addition / − omission)</label>
                  <input type="number" step="0.01" data-testid="vo-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm font-mono" required />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Description *</label>
                <input data-testid="vo-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" required />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Reason / Notes</label>
                <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" rows={2} />
              </div>
              {costCenters.length > 0 && (
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Cost Center (optional)</label>
                  <select value={form.cost_center_id} onChange={(e) => setForm({ ...form, cost_center_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    <option value="">— None —</option>
                    {costCenters.map((cc) => <option key={cc.id} value={cc.id}>{cc.code} • {cc.name}</option>)}
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50">Cancel</button>
                <button type="submit" data-testid="vo-submit" disabled={submitting}
                  className="px-4 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">
                  {submitting ? 'Saving…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariationOrders;
